import { computed, effect, inject, untracked } from '@angular/core';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import { ShellStore } from '@iw/shell';
import {
  DictionariesStore,
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@iw/store';

import { WITHDRAWAL_API } from './api';
import {
  EstimateFeeReq,
  EstimateFeeRes,
  WITHDRAWAL_METHODS,
  WithdrawalReq,
} from './entities';
import { InfoDialogStatus } from '../transfer/components/info-dialog';

interface WithdrawalState {
  operationId: string | null;
  estimation: EstimateFeeRes | null;
}

const initialState: WithdrawalState = {
  operationId: null,
  estimation: null,
};

export const WithdrawalStore = signalStore(
  withDevtools('withdrawal'),
  withState(initialState),
  withRequestStatus(['withdrawal', 'estimateFee']),
  withRequestHandler(),
  withProps((store) => ({
    store,
    shellStore: inject(ShellStore),
    withdrawalApi: inject(WITHDRAWAL_API),
    spinnerService: inject(SpinnerService),
    dictionariesStore: inject(DictionariesStore),
    storeHelperService: inject(StoreHelperService),
  })),
  withComputed(({ store }) => ({
    status: computed<InfoDialogStatus>(() => {
      if (store.isWithdrawalSuccess()) return 'succeeded';
      if (store.withdrawalError()) return 'failed';
      return 'pending';
    }),
    errorText: computed<string>(() => {
      if (!store.withdrawalError()) {
        return '';
      }
      return store.withdrawalError().originalError?.error?.details;
    }),
    withdrawalMethods: computed(() => WITHDRAWAL_METHODS),
  })),
  withMethods(({ store, withdrawalApi }) => ({
    withdrawal: rxMethod<WithdrawalReq>(
      switchMap((params) => {
        patchState(store, setPendingStatusFor('withdrawal'));
        return withdrawalApi.withdrawal(params).pipe(
          tapResponse({
            next: ({ operationId }) => {
              patchState(
                store,
                { operationId },
                setSuccessStatusFor('withdrawal'),
              );
            },
            error: (error: AppError) =>
              patchState(store, setErrorStatusFor('withdrawal', error)),
          }),
        );
      }),
    ),

    estimateFee: rxMethod<EstimateFeeReq>(
      switchMap((params) => {
        patchState(store, setPendingStatusFor('estimateFee'));
        return withdrawalApi.estimateFee(params).pipe(
          tapResponse({
            next: (res) => {
              patchState(
                store,
                { estimation: res },
                setSuccessStatusFor('estimateFee'),
              );
            },
            error: (error: AppError) =>
              patchState(store, setErrorStatusFor('estimateFee', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.dictionariesStore.refreshRates();

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
